<template>
  <main class="flex flex-row w-full">
    <Sidebar />

    <div
      v-if="hasLoaded"
      ref="page-content"
      class="flex flex-row w-full flex-grow h-screen flex-1 max-h-screen bg-surface"
    >
      <NotAccessiblePage
        v-show="!isPageAccessible"
        :feature-name="notAccessibleFeature"
      />
      <!-- main screen content, in below slot - this is what scrolls -->
      <div
        v-show="isPageAccessible"
        class="w-full min-h-screen overflow-y-auto page-content"
        data-test-id="custom-layout-scroll-box"
      >
        <slot />
      </div>
    </div>
    <!-- Modal teleport target -->
    <div id="modal" />
  </main>
</template>

<script lang="ts" setup>
import { useStore } from 'vuex'
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getContext } from '@/utils/general'
import { useGlobalKeymap } from '@/composables/useGlobalKeymap'
import NotAccessiblePage from '@/components/am3/exceptions/NotAccessiblePage.vue'
import Sidebar from '@/components/am3/navigation/Sidebar.vue'

const { $arMediaQuery, $updateArMediaQuery } = getContext()
const store = useStore()
const route = useRoute()

// Initialize global keymap
useGlobalKeymap()

const hasInitializedHubspot = ref(false)
const isPageAccessible = ref(true)
const notAccessibleFeature = ref(null)
const leftSectionWidth = ref('0px')
const hasLoaded = ref(false)

const accessibility = computed(() => store.state.accessibility)
const isAdminAccount = computed(() => store.getters['authModule/isAdminAccount'])
const isSetupPage = computed(() => route.path === '/campaigns/create')

const updateLeftSectionWidth = () => {
  if (window.innerWidth >= $arMediaQuery.sizeMap.md.min) {
    leftSectionWidth.value = '64px'
  }
  else {
    leftSectionWidth.value = '0px'
  }
}

const updateMediaQuery = () => {
  updateLeftSectionWidth()
  $updateArMediaQuery({
    windowWidth: window.innerWidth,
    pageContentWidth: window.innerWidth - parseInt(leftSectionWidth.value, 10),
  })
}

const updatePageAccessibility = (newValue) => {
  isPageAccessible.value = newValue.accessible
  notAccessibleFeature.value = newValue.noAccessibilityTo
}

const dispatchResizeEvent = () => {
  window.dispatchEvent(new Event('resize'))
}

onMounted(() => {
  window.addEventListener('resize', updateMediaQuery)
  updateMediaQuery()
  hasLoaded.value = true
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateMediaQuery)
})

let updatePageAccessibilityTimeout: any = null

watch(accessibility, (newValue) => {
  if (updatePageAccessibilityTimeout) {
    clearTimeout(updatePageAccessibilityTimeout)
  }
  if (newValue?.accessible) {
    updatePageAccessibilityTimeout = setTimeout(() => {
      updatePageAccessibility(newValue)
    }, 50)
  }
  else {
    updatePageAccessibility(newValue)
  }
}, { deep: true })
</script>
